import { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useTranslation } from "@/contexts/LanguageContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import TrackMap, { StartLocationMap } from "@/components/TrackMap";
import { PhotoCarousel } from "@/components/PhotoCarousel";
import { formatEventDate } from "@/utils/dateUtils";
import { HighlightBadge } from "@/components/HighlightBadge";
import { Calendar, MapPin, Mountains, ArrowRight, ArrowLeft, Timer, Path, NavigationArrow, Backpack, Link as LinkIcon, Globe, ShieldCheck, Upload, Lock } from "@phosphor-icons/react";
import SignaturePad from "@/components/SignaturePad";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function generateElevationProfile(km, elevGain, startAlt = 350) {
  if (!km || !elevGain) return [];
  const pts = [];
  const n = Math.max(12, Math.floor(km));
  for (let i = 0; i <= n; i++) {
    const p = i / n;
    const main = Math.sin(p * Math.PI * 0.95) * elevGain * 0.65;
    const mid = Math.sin(p * Math.PI * 2.5) * elevGain * 0.12;
    const small = Math.sin(p * Math.PI * 5) * elevGain * 0.06;
    pts.push({ km: Math.round((km * p) * 10) / 10, alt: Math.round(Math.max(startAlt + main + mid + small, startAlt * 0.9)) });
  }
  return pts;
}

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (<div className="bg-[#1A1A1A] border border-white/10 px-3 py-2 text-xs"><p className="text-white font-bold">{payload[0].value}m</p><p className="text-gray-400">km {payload[0].payload.km}</p></div>);
  }
  return null;
};

export default function EventDetail() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const [loading, setLoading] = useState(true);
  const [regOpen, setRegOpen] = useState(false);
  // Fix: cuando un Radix Select se cierra rápido dentro de un Dialog, deja
  // `pointer-events: none` en <body> o <html>, congelando la pantalla. Usamos
  // un MutationObserver para restaurar la interactividad INSTANTÁNEAMENTE
  // apenas se detecta el estado spurio (más rápido que un setInterval).
  useEffect(() => {
    const clean = () => {
      if (document.body.style.pointerEvents === "none") document.body.style.pointerEvents = "";
      if (document.documentElement.style.pointerEvents === "none") document.documentElement.style.pointerEvents = "";
    };
    clean();
    // Observa cambios en style de <body> y <html> (Radix modifica style inline).
    let obs;
    if (typeof MutationObserver !== "undefined") {
      obs = new MutationObserver(clean);
      obs.observe(document.body, { attributes: true, attributeFilter: ["style"] });
      obs.observe(document.documentElement, { attributes: true, attributeFilter: ["style"] });
    }
    // Backup: cada 300ms por si algo escapa al observer.
    const t = setInterval(clean, 300);
    return () => { if (obs) obs.disconnect(); clearInterval(t); };
  }, []);
  const [regLoading, setRegLoading] = useState(false);
  const [regForm, setRegForm] = useState({ full_name: "", rut: "", phone: "", age: "", gender: "masculino", category: "general", email: "", city: "", club: "", shirt_size: "M", region: "", pais: "", fecha_nacimiento: "", emergencia_nombre: "", emergencia_telefono: "", grupo_sanguineo: "", alergias: "", objetivo: "" });
  // Consentimiento informado (solo si event.consent_text)
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [consentClausesAccepted, setConsentClausesAccepted] = useState({}); // { title: true }
  const [attachments, setAttachments] = useState({}); // { key: {filename, content, content_type, size} }
  const [consentSignature, setConsentSignature] = useState("");
  const [consentFullName, setConsentFullName] = useState("");
  const [consentRut, setConsentRut] = useState("");
  const [guardianName, setGuardianName] = useState("");
  const [guardianRut, setGuardianRut] = useState("");
  const [emerName, setEmerName] = useState("");
  const [emerPhone, setEmerPhone] = useState("");
  const [emerRel, setEmerRel] = useState("");
  const [discountCode, setDiscountCode] = useState("");
  const [discountInfo, setDiscountInfo] = useState(null);
  const [discountLoading, setDiscountLoading] = useState(false);
  // Código de acceso (eventos privados)
  const [accessCode, setAccessCode] = useState("");
  const [accessCodeValid, setAccessCodeValid] = useState(false);
  const [accessCodeInfo, setAccessCodeInfo] = useState(null); // {remaining, max_uses, used, label}
  const [accessCodeError, setAccessCodeError] = useState("");
  const [accessCodeLoading, setAccessCodeLoading] = useState(false);
  const { t, lang } = useTranslation();
  const { formatPrice } = useCurrency();

  useEffect(() => {
    Promise.all([axios.get(`${API}/events/${eventId}`), axios.get(`${API}/products`)])
      .then(([evRes, prodRes]) => { setEvent(evRes.data); setProducts(prodRes.data); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [eventId]);

  // Auto-lee el código desde ?code=XXX en la URL (link compartido pre-llenado).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const c = (params.get("code") || "").trim();
    if (c) setAccessCode(c);
  }, []);

  // Función que valida el código de acceso contra el backend.
  const verifyAccessCode = useCallback(async (codeStr) => {
    const code = (codeStr ?? accessCode).trim();
    if (!code) { setAccessCodeError("Ingresa un código"); return false; }
    setAccessCodeLoading(true);
    setAccessCodeError("");
    try {
      const { data } = await axios.post(`${API}/events/${eventId}/verify-access-code`, { code });
      setAccessCodeValid(true);
      setAccessCodeInfo(data);
      return true;
    } catch (err) {
      setAccessCodeValid(false);
      setAccessCodeInfo(null);
      setAccessCodeError(err.response?.data?.detail || "Código no válido");
      return false;
    } finally {
      setAccessCodeLoading(false);
    }
  }, [accessCode, eventId]);

  // Auto-valida cuando llega ?code=XXX en la URL (una sola vez tras cargar el evento).
  useEffect(() => {
    if (!event) return;
    if (!event.requires_access_code) return;
    if (accessCodeValid) return;
    if (!accessCode) return;
    verifyAccessCode(accessCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event]);

  // Auto-selecciona la categoría según la edad cuando el evento define categorías custom.
  useEffect(() => {
    if (!event) return;
    const custom = (event.categories || []).filter(c => (c.name || "").trim());
    if (custom.length === 0) return;
    const age = Number(regForm.age) || 0;
    if (age <= 0) return;
    const match = custom.find(c => {
      const min = Number(c.min_age) || 0;
      const max = Number(c.max_age) || 0;
      return (min === 0 || age >= min) && (max === 0 || age <= max);
    });
    if (match && regForm.category !== match.name) {
      setRegForm(p => ({ ...p, category: match.name }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, regForm.age]);

  const localeMap = { es: "es-CL", en: "en-US", pt: "pt-BR", fr: "fr-FR" };
  const diffColors = { principiante: "text-green-400 border-green-400/30", intermedio: "text-yellow-400 border-yellow-400/30", avanzado: "text-red-400 border-red-400/30" };

  const selectedDist = selectedIdx >= 0 ? event?.distances?.[selectedIdx] : null;

  const elevationData = useMemo(() => {
    if (!selectedDist) return [];
    if (selectedDist.elevation_profile && selectedDist.elevation_profile.length > 0) return selectedDist.elevation_profile;
    return generateElevationProfile(selectedDist.km, selectedDist.elevation_gain || 0);
  }, [selectedDist]);

  const suggestedProds = useMemo(() => {
    if (!event?.suggested_products) return [];
    return products.filter(p => event.suggested_products.includes(p.id));
  }, [event, products]);

  const labels = {
    back: { es: "Volver a Eventos", en: "Back to Events", fr: "Retour", pt: "Voltar" },
    general_info: { es: "Informacion General", en: "General Information", fr: "Informations generales", pt: "Informacoes gerais" },
    start_location: { es: "Ubicacion de Partida", en: "Start Location", fr: "Lieu de depart", pt: "Local de partida" },
    links_interest: { es: "Links de Interes", en: "Links of Interest", fr: "Liens utiles", pt: "Links de interesse" },
    choose_dist: { es: "Elige tu Distancia", en: "Choose your Distance", fr: "Choisissez votre distance", pt: "Escolha sua distancia" },
    elevation_profile: { es: "Perfil de Desnivel", en: "Elevation Profile", fr: "Profil d'altitude", pt: "Perfil de elevacao" },
    route_map: { es: "Mapa del Recorrido", en: "Route Map", fr: "Carte du parcours", pt: "Mapa do percurso" },
    route_desc: { es: "Descripcion de la Ruta", en: "Route Description", fr: "Description du parcours", pt: "Descricao do percurso" },
    suggested: { es: "Material Sugerido", en: "Suggested Gear", fr: "Equipement suggere", pt: "Material Sugerido" },
    elev_gain: { es: "Desnivel +", en: "Elevation +", fr: "Denivele +", pt: "Desnivel +" },
    from: { es: "Desde", en: "From", fr: "A partir de", pt: "A partir de" },
  };
  const l = (k) => labels[k]?.[lang] || labels[k]?.["es"] || k;

  if (loading) return <main className="pt-20 min-h-screen flex items-center justify-center"><p className="text-gray-400">{t("loading")}</p></main>;
  if (!event) return <main className="pt-20 min-h-screen flex items-center justify-center"><p className="text-gray-400">Evento no encontrado</p></main>;

  // Gate para eventos privados con privacy_mode="hidden" (oculto del listado).
  // La página muestra sólo título + gate de código; nada de detalles hasta validar.
  const isPrivateHidden = event.requires_access_code && event.privacy_mode === "hidden";
  const isPrivateGated = event.requires_access_code && event.privacy_mode !== "hidden";
  if (isPrivateHidden && !accessCodeValid) {
    return (
      <main className="pt-24 min-h-screen flex items-center justify-center px-4" data-testid="event-private-gate">
        <div className="max-w-md w-full bg-[#121212] border border-white/10 p-8">
          <div className="flex items-center gap-3 mb-4">
            <Lock size={24} className="text-[#FF4D00]" />
            <h1 className="text-2xl font-black font-['Cabinet_Grotesk']">Evento privado</h1>
          </div>
          <h2 className="text-lg text-gray-200 mb-1">{event.title}</h2>
          <p className="text-sm text-gray-500 mb-6">Este evento requiere un código de acceso. Contacta a Pro Vertical si crees que deberías tenerlo.</p>
          <Label className="text-xs text-gray-400 uppercase tracking-wider">Código de acceso</Label>
          <Input
            className="bg-[#1A1A1A] border-white/10 mt-2 text-center tracking-widest uppercase"
            value={accessCode}
            onChange={e => setAccessCode(e.target.value.toUpperCase())}
            placeholder="EJ. VIP2026"
            data-testid="private-gate-input"
            onKeyDown={e => { if (e.key === "Enter") verifyAccessCode(); }}
          />
          {accessCodeError && <p className="text-xs text-red-400 mt-2" data-testid="private-gate-error">{accessCodeError}</p>}
          <Button
            className="w-full bg-[#FF4D00] hover:bg-[#E64500] text-white font-bold h-12 mt-4"
            onClick={() => verifyAccessCode()}
            disabled={accessCodeLoading}
            data-testid="private-gate-submit"
          >
            {accessCodeLoading ? "Validando..." : "Ingresar al evento"}
          </Button>
          <Link to="/eventos" className="block text-center text-xs text-gray-500 hover:text-white mt-4">Volver a eventos</Link>
        </div>
      </main>
    );
  }

  const eventLinks = event.links || [];
  if (event.drive_link && !eventLinks.find(l => l.url === event.drive_link)) {
    eventLinks.unshift({ label: "Google Drive", url: event.drive_link });
  }

  // Evento gratis: cuando todas las distancias (o el precio base) son 0
  const _distPrices = (event.distances || []).map(d => Number(d.price || 0));
  const _minPrice = _distPrices.length > 0 ? Math.min(..._distPrices) : Number(event.price || 0);
  const eventIsFree = _minPrice === 0;
  const distIsFree = !!selectedDist && Number(selectedDist.price || 0) === 0;

  return (
    <main className="pt-16 min-h-screen" data-testid="event-detail-page">
      {/* Hero */}
      <section className="relative h-72 sm:h-96 overflow-hidden">
        <img src={event.image_url} alt={event.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent" />
        <HighlightBadge text={event.badge_text} color={event.badge_color} className="top-4 left-4 sm:top-6 sm:left-6 text-xs sm:text-sm px-3 sm:px-4 py-1 sm:py-1.5" />
        {eventIsFree && (
          <span
            data-testid="free-event-badge-detail"
            className="absolute top-4 right-4 sm:top-6 sm:right-6 bg-[#16A34A] text-white text-xs sm:text-sm font-black uppercase tracking-wider px-3 sm:px-4 py-1 sm:py-1.5 shadow-lg shadow-black/40 z-10"
          >
            {t("free_label")}
          </span>
        )}
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 max-w-7xl mx-auto">
          <Link to="/eventos" className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-[#FF4D00] transition-colors mb-4" data-testid="event-back-link">
            <ArrowLeft size={14} /> {l("back")}
          </Link>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <Badge variant="outline" className={`text-xs ${diffColors[event.difficulty] || ""}`}>{event.difficulty}</Badge>
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-white/5 border border-white/10 px-2.5 py-1">
              <Calendar size={14} className="text-[#FF4D00]" />
              {formatEventDate(event.date, localeMap[lang] || "es-CL", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
              {event.start_time && <span className="text-[#FF4D00]"> · {event.start_time} hrs</span>}
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase font-['Cabinet_Grotesk'] tracking-tight inline-flex items-center gap-3 flex-wrap" data-testid="event-detail-title">
            {event.title}
            {eventIsFree && (
              <span className="bg-[#16A34A] text-white text-xs sm:text-sm font-black uppercase tracking-wider px-2.5 py-1 rounded-sm align-middle">{t("free_label")}</span>
            )}
          </h1>
          <div className="flex items-center gap-3 mt-3 text-sm text-gray-400">
            <span className="flex items-center gap-1"><MapPin size={14} weight="fill" /> {event.location}</span>
            {event.elevation_m && <span className="flex items-center gap-1"><Mountains size={14} /> {event.elevation_m}m D+</span>}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        {/* ═══ GENERAL INFO SECTION ═══ */}
        <section className="mb-12">
          {event.show_description !== false && (
            <>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-gray-500 mb-4">{l("general_info")}</p>
              <p className="text-base text-gray-400 leading-relaxed max-w-3xl mb-8 whitespace-pre-wrap" data-testid="event-detail-description">{event.description}</p>
            </>
          )}

          {/* Event Photos (max 10) - galeria con carrusel + lightbox */}
          {event.photos && event.photos.length > 0 && (
            <div className="mb-8 max-w-4xl mx-auto">
              <PhotoCarousel
                photos={event.photos.slice(0, 10).map(p => typeof p === "string" ? p : p.url).filter(Boolean)}
                aspectClass="aspect-[3/2]"
                objectFit="contain"
                altPrefix={event.title}
              />
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Start Location Map */}
            {(event.start_lat && event.start_lng) && (
              <div className="bg-[#121212] border border-white/5 overflow-hidden">
                <p className="text-xs tracking-[0.15em] uppercase font-bold text-gray-500 px-5 pt-4 pb-2 flex items-center gap-1">
                  <MapPin size={14} /> {l("start_location")}
                </p>
                <StartLocationMap lat={event.start_lat} lng={event.start_lng} name={event.location} height="h-52" />
              </div>
            )}

            {/* Links of Interest */}
            {(eventLinks.length > 0 || event.location_url) && (
              <div className="bg-[#121212] border border-white/5 p-5">
                <p className="text-xs tracking-[0.15em] uppercase font-bold text-gray-500 mb-4 flex items-center gap-1">
                  <LinkIcon size={14} /> {l("links_interest")}
                </p>
                <div className="space-y-2">
                  {eventLinks.map((link, idx) => (
                    <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-gray-300 hover:text-[#FF4D00] transition-colors p-2 bg-[#1A1A1A] border border-white/5 hover:border-[#FF4D00]/30">
                      <Globe size={16} className="flex-shrink-0 text-[#FF4D00]" /> {link.label || link.url}
                    </a>
                  ))}
                  {event.location_url && (
                    <a href={event.location_url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-gray-300 hover:text-[#FF4D00] transition-colors p-2 bg-[#1A1A1A] border border-white/5 hover:border-[#FF4D00]/30">
                      <NavigationArrow size={16} weight="fill" className="flex-shrink-0 text-[#FF4D00]" /> {lang === "en" ? "View Location" : "Ver Ubicacion"}
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Suggested Products se muestran debajo del selector de distancias (más abajo en el orden visual) */}
        </section>

        {/* ═══ DISTANCES SECTION ═══ */}
        {event.distances && event.distances.length > 0 && (
          <section>
            <div className="border-t border-white/5 pt-10 mb-8">
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-gray-500 mb-4 flex items-center gap-2">
                <Path size={16} /> {l("choose_dist")}
              </p>
              <div className="flex flex-wrap gap-3" data-testid="distance-selector">
                {event.distances.map((dist, idx) => {
                  const maxP = Number(dist.max_participants || 0);
                  const regCount = Number(dist.registered_count || 0);
                  const isFull = dist.is_full || (maxP > 0 && regCount >= maxP);
                  const remaining = maxP > 0 ? Math.max(0, maxP - regCount) : null;
                  const lowStock = remaining !== null && remaining > 0 && remaining < 5;
                  return (
                  <button key={idx} onClick={() => setSelectedIdx(idx)}
                    className={`relative px-6 py-3 border transition-all duration-200 ${selectedIdx === idx ? "bg-[#FF4D00] border-[#FF4D00] text-white" : "bg-[#121212] border-white/10 text-gray-300 hover:border-[#FF4D00]/50"} ${isFull ? "opacity-70" : ""}`}
                    data-testid={`distance-tab-${dist.label}`}
                  >
                    {isFull && (
                      <span className="absolute -top-2 right-1 bg-red-600 text-white text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 shadow-md" data-testid={`distance-full-badge-${dist.label}`}>
                        Agotado
                      </span>
                    )}
                    {!isFull && lowStock && (
                      <span className="absolute -top-2 right-1 bg-amber-500 text-black text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 shadow-md" data-testid={`distance-low-badge-${dist.label}`}>
                        ¡Últimos {remaining}!
                      </span>
                    )}
                    <span className="text-xl font-black font-['Cabinet_Grotesk']">{dist.label}</span>
                    <span className="block text-xs mt-0.5 opacity-70">{formatPrice(dist.price)}</span>
                  </button>
                  );
                })}
              </div>
            </div>

            {/* Suggested Products - debajo del selector de distancias */}
            {suggestedProds.length > 0 && (
              <div className="mb-8" data-testid="suggested-products-section">
                <p className="text-xs tracking-[0.15em] uppercase font-bold text-gray-500 mb-4 flex items-center gap-1"><Backpack size={14} /> {l("suggested")}</p>
                <div className="flex gap-4 overflow-x-auto pb-2">
                  {suggestedProds.map(prod => (
                    <Link key={prod.id} to="/tienda" className="flex-shrink-0 w-40 group">
                      <div className="w-40 h-32 bg-[#1A1A1A] border border-white/5 overflow-hidden mb-2 group-hover:border-[#FF4D00]/40 transition-colors">
                        <img src={prod.image_url} alt={prod.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      </div>
                      <p className="text-xs font-medium text-gray-300 truncate">{prod.name}</p>
                      <p className="text-xs font-bold text-[#FF4D00]">{formatPrice(prod.price)}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Selected Distance Content */}
            {selectedDist && (
              <motion.div key={selectedIdx} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                {/* Stats */}
                <div className="flex flex-wrap gap-4 mb-8">
                  <div className="bg-[#121212] border border-white/5 px-5 py-3">
                    <p className="text-xs text-gray-500">{lang === "en" ? "Distance" : "Distancia"}</p>
                    <p className="text-lg font-bold font-['Cabinet_Grotesk']">{selectedDist.km} km</p>
                  </div>
                  {selectedDist.elevation_gain > 0 && (
                    <div className="bg-[#121212] border border-white/5 px-5 py-3">
                      <p className="text-xs text-gray-500">{l("elev_gain")}</p>
                      <p className="text-lg font-bold font-['Cabinet_Grotesk'] text-[#FF4D00]">+{selectedDist.elevation_gain}m</p>
                    </div>
                  )}
                  <div className="bg-[#121212] border border-white/5 px-5 py-3">
                    <p className="text-xs text-gray-500">{t("inscription")}</p>
                    <p className="text-lg font-bold font-['Cabinet_Grotesk'] text-[#FF4D00]">{formatPrice(selectedDist.price)}</p>
                  </div>
                </div>

                {/* Interactive Map + Elevation side by side */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                  {/* Track Map (from GPX) or static image */}
                  {selectedDist.track_data && selectedDist.track_data.length > 0 ? (
                    <div className="bg-[#121212] border border-white/5 overflow-hidden" data-testid="interactive-track-map">
                      <p className="text-xs tracking-[0.15em] uppercase font-bold text-gray-500 px-5 pt-4 pb-2 flex items-center gap-1"><MapPin size={14} /> {l("route_map")}</p>
                      <TrackMap track={selectedDist.track_data} markers={selectedDist.markers || []} height="h-72" />
                    </div>
                  ) : selectedDist.map_url ? (
                    <div className="bg-[#121212] border border-white/5 p-5">
                      <p className="text-xs tracking-[0.15em] uppercase font-bold text-gray-500 mb-4">{l("route_map")}</p>
                      <a href={selectedDist.map_url} target="_blank" rel="noopener noreferrer"><img src={selectedDist.map_url} alt="Mapa" className="w-full h-64 object-contain bg-[#0e0e0e] hover:opacity-90 transition-opacity" /></a>
                    </div>
                  ) : null}

                  {/* Elevation Profile */}
                  {elevationData.length > 0 && (
                    <div className="bg-[#121212] border border-white/5 p-5" data-testid="elevation-profile">
                      <p className="text-xs tracking-[0.15em] uppercase font-bold text-gray-500 mb-4 flex items-center gap-1"><Mountains size={14} /> {l("elevation_profile")}</p>
                      <ResponsiveContainer width="100%" height={250}>
                        <AreaChart data={elevationData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                          <defs><linearGradient id="altGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#FF4D00" stopOpacity={0.4} /><stop offset="95%" stopColor="#FF4D00" stopOpacity={0.02} /></linearGradient></defs>
                          <XAxis dataKey="km" tick={{ fill: "#666", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#333" }} />
                          <YAxis tick={{ fill: "#666", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#333" }} />
                          <Tooltip content={<CustomTooltip />} />
                          <Area type="monotone" dataKey="alt" stroke="#FF4D00" strokeWidth={2} fill="url(#altGrad)" dot={false} activeDot={{ r: 4, fill: "#FF4D00", stroke: "#fff", strokeWidth: 2 }} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>

                {/* Route Description */}
                {selectedDist.description && (
                  <div className="bg-[#121212] border border-white/5 p-6 mb-8" data-testid="route-description">
                    <p className="text-xs tracking-[0.15em] uppercase font-bold text-gray-500 mb-3">{l("route_desc")}</p>
                    <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-wrap">{selectedDist.description}</p>
                  </div>
                )}

                {/* Distance Photos (max 5) - carrusel */}
                {selectedDist.photos && selectedDist.photos.length > 0 && (
                  <div className="mb-8 max-w-3xl mx-auto">
                    <PhotoCarousel
                      photos={selectedDist.photos.slice(0, 5).map(p => typeof p === "string" ? p : p.url).filter(Boolean)}
                      aspectClass="aspect-[3/2]"
                      objectFit="contain"
                      altPrefix={`${selectedDist.label}`}
                    />
                  </div>
                )}

                {/* Register CTA */}
                {(() => {
                  const maxP = Number(selectedDist.max_participants || 0);
                  const regCount = Number(selectedDist.registered_count || 0);
                  const isFull = selectedDist.is_full || (maxP > 0 && regCount >= maxP);
                  const remaining = maxP > 0 ? Math.max(0, maxP - regCount) : null;
                  const lowStock = remaining !== null && remaining > 0 && remaining < 5;
                  return (
                <div className={`flex flex-col sm:flex-row items-center justify-between bg-[#121212] border p-6 mb-10 ${isFull ? "border-red-500/30" : "border-[#FF4D00]/20"}`}>
                  <div>
                    <p className="text-sm text-gray-400 mb-1">{t("inscription")} - {selectedDist.label}</p>
                    {distIsFree ? (
                      <span className="text-3xl font-black text-[#16A34A] font-['Cabinet_Grotesk']">{t("free_label")}</span>
                    ) : (
                      <span className="text-3xl font-black text-[#FF4D00] font-['Cabinet_Grotesk']">{formatPrice(selectedDist.price)}</span>
                    )}
                    {isFull && <p className="text-xs text-red-400 font-bold mt-1 uppercase tracking-wider" data-testid="register-full-msg">Distancia agotada · sin cupos</p>}
                    {!isFull && lowStock && <p className="text-xs text-amber-400 font-bold mt-1" data-testid="register-low-msg">¡Solo quedan {remaining} cupos!</p>}
                    {isPrivateGated && (
                      <p className="text-xs text-amber-300 font-bold mt-1 inline-flex items-center gap-1" data-testid="event-private-badge">
                        <Lock size={12} /> Evento privado · requiere código {accessCodeValid && <span className="text-green-400">✓ código validado</span>}
                      </p>
                    )}
                  </div>
                  {isFull ? (
                    <Button disabled className="bg-gray-700 text-gray-400 font-bold h-14 px-8 text-base mt-4 sm:mt-0 cursor-not-allowed" data-testid="detail-register-btn-disabled">
                      Sin cupos
                    </Button>
                  ) : (
                  <Button className={`${distIsFree ? "bg-[#16A34A] hover:bg-[#15803D]" : "bg-[#FF4D00] hover:bg-[#E64500]"} text-white font-bold h-14 px-8 text-base mt-4 sm:mt-0`}
                    onClick={() => setRegOpen(true)} data-testid="detail-register-btn">
                    {distIsFree ? t("register_free") : `${t("register")} ${selectedDist.label}`} <ArrowRight size={16} className="ml-2" />
                  </Button>
                  )}
                </div>
                  );
                })()}
              </motion.div>
            )}

            {/* Prompt to select distance */}
            {selectedIdx < 0 && (
              <div className="text-center py-12 text-gray-500">
                <Path size={40} className="mx-auto mb-3 opacity-30" />
                <p>{lang === "en" ? "Select a distance to view details" : "Selecciona una distancia para ver los detalles"}</p>
              </div>
            )}
          </section>
        )}
      </div>

      {/* Registration Form Dialog */}
      <Dialog open={regOpen} onOpenChange={setRegOpen}>
        <DialogContent
          className="bg-[#121212] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto"
          data-testid="registration-dialog"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="font-['Cabinet_Grotesk'] text-xl">{t("inscription")} - {selectedDist?.label}</DialogTitle>
            <DialogDescription>{event?.title}</DialogDescription>
          </DialogHeader>
          {/* Gate de código para eventos privados (modo "gated"): antes de mostrar el form completo. */}
          {event?.requires_access_code && !accessCodeValid ? (
            <div className="space-y-3" data-testid="reg-access-code-gate">
              <div className="bg-amber-500/10 border border-amber-500/30 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Lock size={16} className="text-amber-400" />
                  <p className="text-sm font-bold text-amber-200">Evento privado</p>
                </div>
                <p className="text-xs text-gray-300">Este evento requiere un código de acceso para inscribirse. Ingresa el código que te enviaron.</p>
              </div>
              <div>
                <Label className="text-xs text-gray-400 uppercase tracking-wider">Código de acceso *</Label>
                <Input
                  className="bg-[#1A1A1A] border-white/10 mt-2 text-center tracking-widest uppercase"
                  value={accessCode}
                  onChange={e => setAccessCode(e.target.value.toUpperCase())}
                  placeholder="EJ. VIP2026"
                  data-testid="reg-access-code-input"
                  onKeyDown={e => { if (e.key === "Enter") verifyAccessCode(); }}
                />
                {accessCodeError && <p className="text-xs text-red-400 mt-2" data-testid="reg-access-code-error">{accessCodeError}</p>}
              </div>
              <Button
                className="w-full bg-[#FF4D00] hover:bg-[#E64500] text-white font-bold h-12"
                onClick={() => verifyAccessCode()}
                disabled={accessCodeLoading}
                data-testid="reg-access-code-submit"
              >
                {accessCodeLoading ? "Validando..." : "Validar y continuar"}
              </Button>
            </div>
          ) : (
          <div className="space-y-3">
            {event?.requires_access_code && accessCodeValid && (
              <div className="bg-green-500/10 border border-green-500/30 p-2 flex items-center justify-between" data-testid="reg-access-code-valid-banner">
                <p className="text-xs text-green-300">✓ Código validado{accessCodeInfo?.label ? `: ${accessCodeInfo.label}` : ""}{accessCodeInfo?.remaining !== null && accessCodeInfo?.remaining !== undefined ? ` · ${accessCodeInfo.remaining} cupos restantes` : ""}</p>
                <button
                  type="button"
                  onClick={() => { setAccessCodeValid(false); setAccessCode(""); setAccessCodeInfo(null); }}
                  className="text-[10px] uppercase tracking-wider text-gray-400 hover:text-white"
                >
                  Cambiar
                </button>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              {/* Campos OBLIGATORIOS siempre */}
              <div className="col-span-2"><Label className="text-xs text-gray-400">{lang === "en" ? "Full Name" : "Nombre Completo"} *</Label>
                <Input className="bg-[#1A1A1A] border-white/10 mt-1" value={regForm.full_name} onChange={e => setRegForm(p => ({ ...p, full_name: e.target.value }))} data-testid="reg-fullname" /></div>
              <div className="col-span-2"><Label className="text-xs text-gray-400">Email *</Label>
                <Input type="email" className="bg-[#1A1A1A] border-white/10 mt-1" value={regForm.email} onChange={e => setRegForm(p => ({ ...p, email: e.target.value }))} data-testid="reg-email" /></div>
              <div className="col-span-2"><Label className="text-xs text-gray-400">{lang === "en" ? "Phone" : "Celular"} *</Label>
                <Input className="bg-[#1A1A1A] border-white/10 mt-1" value={regForm.phone} onChange={e => setRegForm(p => ({ ...p, phone: e.target.value }))} placeholder="+56 9..." data-testid="reg-phone" /></div>

              {/* Sexo - obligatorio para categorias deportivas */}
              <div><Label className="text-xs text-gray-400">{lang === "en" ? "Gender" : "Sexo"} *</Label>
                <Select value={regForm.gender} onValueChange={v => { setTimeout(() => setRegForm(p => ({ ...p, gender: v })), 0); }}>
                  <SelectTrigger className="bg-[#1A1A1A] border-white/10 mt-1" data-testid="reg-gender"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="masculino">{lang === "en" ? "Male" : "Masculino"}</SelectItem>
                    <SelectItem value="femenino">{lang === "en" ? "Female" : "Femenino"}</SelectItem>
                    <SelectItem value="otro">{lang === "en" ? "Other" : "Otro"}</SelectItem>
                  </SelectContent>
                </Select></div>
              <div><Label className="text-xs text-gray-400">{lang === "en" ? "Category" : "Categoria"} *</Label>
                {(() => {
                  const custom = (event.categories || []).filter(c => (c.name || "").trim());
                  const age = Number(regForm.age) || 0;
                  // Auto-select matching category from custom list based on age.
                  if (custom.length > 0) {
                    const match = custom.find(c => {
                      const min = Number(c.min_age) || 0;
                      const max = Number(c.max_age) || 0;
                      return (min === 0 || age >= min) && (max === 0 || age <= max);
                    });
                    return (
                      <Select value={regForm.category} onValueChange={v => { setTimeout(() => setRegForm(p => ({ ...p, category: v })), 0); }}>
                        <SelectTrigger className="bg-[#1A1A1A] border-white/10 mt-1" data-testid="reg-category"><SelectValue placeholder={lang === "en" ? "Choose category" : "Elige categoría"} /></SelectTrigger>
                        <SelectContent>
                          {custom.map((c, i) => {
                            const min = Number(c.min_age) || 0;
                            const max = Number(c.max_age) || 0;
                            const range = min > 0 || max > 0 ? ` (${min || "–"}–${max || "∞"})` : "";
                            return <SelectItem key={i} value={c.name}>{c.name}{range}{match && match.name === c.name ? " ✓" : ""}</SelectItem>;
                          })}
                        </SelectContent>
                      </Select>
                    );
                  }
                  return (
                    <Select value={regForm.category} onValueChange={v => { setTimeout(() => setRegForm(p => ({ ...p, category: v })), 0); }}>
                      <SelectTrigger className="bg-[#1A1A1A] border-white/10 mt-1" data-testid="reg-category"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General</SelectItem><SelectItem value="sub23">Sub 23</SelectItem>
                        <SelectItem value="master">Master (40+)</SelectItem><SelectItem value="senior_master">Senior Master (50+)</SelectItem>
                        <SelectItem value="gran_master">Gran Master (60+)</SelectItem>
                      </SelectContent>
                    </Select>
                  );
                })()}
              </div>

              {/* Campos OPCIONALES configurables por evento */}
              {(event.registration_fields || []).includes("rut") && (
                <div className="col-span-2"><Label className="text-xs text-gray-400">RUT/DNI *</Label>
                  <Input className="bg-[#1A1A1A] border-white/10 mt-1" value={regForm.rut} onChange={e => setRegForm(p => ({ ...p, rut: e.target.value }))} placeholder="12.345.678-9" data-testid="reg-rut" /></div>
              )}
              {(event.registration_fields || []).includes("edad") && (
                <div><Label className="text-xs text-gray-400">{lang === "en" ? "Age" : "Edad"} *</Label>
                  <Input type="number" className="bg-[#1A1A1A] border-white/10 mt-1" value={regForm.age} onChange={e => setRegForm(p => ({ ...p, age: e.target.value }))} data-testid="reg-age" /></div>
              )}
              {(event.registration_fields || []).includes("fecha_nacimiento") && (
                <div><Label className="text-xs text-gray-400">{lang === "en" ? "Birth date" : "Fecha de nacimiento"} *</Label>
                  <Input type="date" className="bg-[#1A1A1A] border-white/10 mt-1" value={regForm.fecha_nacimiento} onChange={e => setRegForm(p => ({ ...p, fecha_nacimiento: e.target.value }))} data-testid="reg-fecha-nac" /></div>
              )}
              {(event.registration_fields || []).includes("club") && (
                <div className="col-span-2"><Label className="text-xs text-gray-400">Club *</Label>
                  <Input className="bg-[#1A1A1A] border-white/10 mt-1" value={regForm.club} onChange={e => setRegForm(p => ({ ...p, club: e.target.value }))} data-testid="reg-club" /></div>
              )}
              {(event.registration_fields || []).includes("region") && (
                <div><Label className="text-xs text-gray-400">{lang === "en" ? "Region" : "Region"} *</Label>
                  <Input className="bg-[#1A1A1A] border-white/10 mt-1" value={regForm.region} onChange={e => setRegForm(p => ({ ...p, region: e.target.value }))} data-testid="reg-region" /></div>
              )}
              {(event.registration_fields || []).includes("pais") && (
                <div><Label className="text-xs text-gray-400">{lang === "en" ? "Country" : "Pais"} *</Label>
                  <Input className="bg-[#1A1A1A] border-white/10 mt-1" value={regForm.pais} onChange={e => setRegForm(p => ({ ...p, pais: e.target.value }))} data-testid="reg-pais" /></div>
              )}
              {(event.registration_fields || []).includes("ciudad") && (
                <div><Label className="text-xs text-gray-400">{lang === "en" ? "City" : "Ciudad"} *</Label>
                  <Input className="bg-[#1A1A1A] border-white/10 mt-1" value={regForm.city} onChange={e => setRegForm(p => ({ ...p, city: e.target.value }))} data-testid="reg-city" /></div>
              )}
              {(event.registration_fields || []).includes("talla_remera") && (
                <div><Label className="text-xs text-gray-400">{lang === "en" ? "Shirt Size" : "Talla Polera"} *</Label>
                  <Select value={regForm.shirt_size} onValueChange={v => { setTimeout(() => setRegForm(p => ({ ...p, shirt_size: v })), 0); }}>
                    <SelectTrigger className="bg-[#1A1A1A] border-white/10 mt-1" data-testid="reg-shirtsize"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="XS">XS</SelectItem><SelectItem value="S">S</SelectItem><SelectItem value="M">M</SelectItem><SelectItem value="L">L</SelectItem><SelectItem value="XL">XL</SelectItem></SelectContent>
                  </Select></div>
              )}
              {(event.registration_fields || []).includes("emergencia_nombre") && (
                <div className="col-span-2"><Label className="text-xs text-gray-400">{lang === "en" ? "Emergency contact name" : "Contacto de emergencia"} *</Label>
                  <Input className="bg-[#1A1A1A] border-white/10 mt-1" value={regForm.emergencia_nombre} onChange={e => setRegForm(p => ({ ...p, emergencia_nombre: e.target.value }))} data-testid="reg-emerg-nombre" /></div>
              )}
              {(event.registration_fields || []).includes("emergencia_telefono") && (
                <div className="col-span-2"><Label className="text-xs text-gray-400">{lang === "en" ? "Emergency phone" : "Telefono emergencia"} *</Label>
                  <Input className="bg-[#1A1A1A] border-white/10 mt-1" value={regForm.emergencia_telefono} onChange={e => setRegForm(p => ({ ...p, emergencia_telefono: e.target.value }))} placeholder="+56 9..." data-testid="reg-emerg-tel" /></div>
              )}
              {(event.registration_fields || []).includes("grupo_sanguineo") && (
                <div><Label className="text-xs text-gray-400">{lang === "en" ? "Blood type" : "Grupo sanguineo"}</Label>
                  <Select value={regForm.grupo_sanguineo} onValueChange={v => { setTimeout(() => setRegForm(p => ({ ...p, grupo_sanguineo: v })), 0); }}>
                    <SelectTrigger className="bg-[#1A1A1A] border-white/10 mt-1" data-testid="reg-blood"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{["O+","O-","A+","A-","B+","B-","AB+","AB-"].map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                  </Select></div>
              )}
              {(event.registration_fields || []).includes("alergias") && (
                <div className="col-span-2"><Label className="text-xs text-gray-400">{lang === "en" ? "Allergies / medical" : "Alergias / condiciones medicas"}</Label>
                  <Input className="bg-[#1A1A1A] border-white/10 mt-1" value={regForm.alergias} onChange={e => setRegForm(p => ({ ...p, alergias: e.target.value }))} placeholder={lang === "en" ? "None or specify" : "Ninguna o especificar"} data-testid="reg-alergias" /></div>
              )}
              {(event.registration_fields || []).includes("objetivo") && (
                <div className="col-span-2"><Label className="text-xs text-gray-400">{lang === "en" ? "Goal in this event" : "Cual es tu objetivo en el evento?"}</Label>
                  <Input className="bg-[#1A1A1A] border-white/10 mt-1" value={regForm.objetivo} onChange={e => setRegForm(p => ({ ...p, objetivo: e.target.value }))} placeholder={lang === "en" ? "Optional" : "Opcional"} data-testid="reg-objetivo" /></div>
              )}
            </div>
            <div className="pt-3 border-t border-white/10">
              {/* Discount Code */}
              <div className="mb-3">
                <Label className="text-xs text-gray-400">{lang === "en" ? "Discount Code" : "Codigo de Descuento"}</Label>
                <div className="flex gap-2 mt-1">
                  <Input className="bg-[#1A1A1A] border-white/10 flex-1 uppercase" value={discountCode} onChange={e => { setDiscountCode(e.target.value); setDiscountInfo(null); }} placeholder="CODIGO" data-testid="reg-discount-input" />
                  <Button variant="outline" size="sm" className="border-white/10 text-gray-300 h-10" disabled={discountLoading} data-testid="reg-discount-btn"
                    onClick={async () => { if (!discountCode.trim()) return; setDiscountLoading(true); try { const { data } = await axios.post(`${API}/discount-codes/validate`, { code: discountCode, item_type: "event", item_id: event?.id || "", original_price: selectedDist?.price || 0 }); setDiscountInfo(data); toast.success(`Descuento: ${data.description}`); } catch (err) { toast.error(err.response?.data?.detail || "Invalido"); setDiscountInfo(null); } finally { setDiscountLoading(false); } }}>
                    {discountLoading ? "..." : (lang === "en" ? "Apply" : "Aplicar")}
                  </Button>
                </div>
                {discountInfo && <p className="text-xs text-green-400 mt-1">{discountInfo.description} (-{formatPrice(discountInfo.discount_amount)})</p>}
              </div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm text-gray-400">{selectedDist?.label} - {event?.title}</span>
                {distIsFree ? (
                  <span className="text-xl font-bold text-[#16A34A]">{t("free_label")}</span>
                ) : (
                  <span className={discountInfo ? "text-sm line-through text-gray-500" : "text-xl font-bold text-[#FF4D00]"}>{formatPrice(selectedDist?.price || 0)}</span>
                )}
              </div>
              {discountInfo && <div className="flex justify-between items-center mb-3"><span className="text-green-400 text-sm">{lang === "en" ? "With discount" : "Con descuento"}</span><span className="text-xl font-bold text-[#FF4D00]">{formatPrice(discountInfo.final_price)}</span></div>}

              {/* Documentos adjuntos solicitados por el evento */}
              {((event?.registration_attachments || []).length > 0) && (
                <div className="mb-4 pt-3 border-t border-white/10" data-testid="reg-attachments-block">
                  <div className="flex items-center gap-2 mb-2">
                    <Upload size={16} className="text-[#FF4D00]" />
                    <Label className="text-sm font-semibold">{lang === "en" ? "Required Documents" : "Documentos solicitados"}</Label>
                  </div>
                  <div className="space-y-3">
                    {(event.registration_attachments || []).map((slot, idx) => {
                      const key = (slot.key || "").trim();
                      if (!key) return null;
                      const val = attachments[key];
                      return (
                        <div key={idx} className="bg-[#0F0F0F] border border-white/10 p-3" data-testid={`reg-attachment-slot-${idx}`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-bold text-white">
                              {slot.label}{slot.required ? " *" : ""}
                            </span>
                            {val && (
                              <button
                                type="button"
                                className="text-red-400 text-[10px] hover:text-red-300"
                                onClick={() => setAttachments(p => { const n = { ...p }; delete n[key]; return n; })}
                                data-testid={`reg-attachment-remove-${idx}`}
                              >
                                Quitar
                              </button>
                            )}
                          </div>
                          {(slot.description || "").trim() && (
                            <div className="text-[11px] text-gray-400 mb-2 whitespace-pre-wrap">{slot.description}</div>
                          )}
                          {val ? (
                            <div className="flex items-center gap-2 text-xs text-green-400 bg-green-500/10 border border-green-500/30 px-2 py-1.5">
                              <span className="truncate flex-1" data-testid={`reg-attachment-filename-${idx}`}>✓ {val.filename}</span>
                              <span className="text-gray-500 text-[10px]">{Math.round((val.size || 0) / 1024)} KB</span>
                            </div>
                          ) : (
                            <label className="cursor-pointer inline-flex items-center gap-2 border border-dashed border-white/20 hover:border-[#FF4D00]/60 px-3 py-2 text-xs text-gray-300">
                              <Upload size={12} />
                              <span>{lang === "en" ? "Choose file" : "Elegir archivo"}</span>
                              <input
                                type="file"
                                accept={slot.accept || undefined}
                                className="hidden"
                                data-testid={`reg-attachment-input-${idx}`}
                                onChange={async e => {
                                  const file = e.target.files && e.target.files[0];
                                  if (!file) return;
                                  if (file.size > 5 * 1024 * 1024) { toast.error(lang === "en" ? "Max 5MB" : "Archivo excede 5MB"); return; }
                                  const reader = new FileReader();
                                  reader.onload = () => {
                                    setAttachments(p => ({
                                      ...p,
                                      [key]: { filename: file.name, content: reader.result, content_type: file.type, size: file.size },
                                    }));
                                  };
                                  reader.readAsDataURL(file);
                                }}
                              />
                            </label>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── Consentimiento informado (si el evento lo requiere) ── */}
              {(((event?.consent_text || "").trim()) || ((event?.consent_clauses || []).length > 0)) && (
                <div className="mb-4 pt-3 border-t border-white/10" data-testid="reg-consent-block">
                  <div className="flex items-center gap-2 mb-2">
                    <ShieldCheck size={16} className="text-[#FF4D00]" />
                    <Label className="text-sm font-semibold">{lang === "en" ? "Informed Consent" : "Consentimiento Informado"}</Label>
                  </div>

                  {(event.consent_text || "").trim() && (
                    <div className="whitespace-pre-wrap text-xs text-gray-300 leading-relaxed max-h-32 overflow-y-auto p-3 bg-[#0F0F0F] border border-white/10 mb-3" data-testid="reg-consent-text">
                      {event.consent_text}
                    </div>
                  )}

                  {/* Cláusulas (checkboxes individuales) */}
                  {(event.consent_clauses || []).length > 0 ? (
                    <div className="space-y-2 mb-3" data-testid="reg-consent-clauses">
                      {(event.consent_clauses || []).map((cl, idx) => {
                        const title = (cl.title || "").trim();
                        if (!title) return null;
                        const checked = !!consentClausesAccepted[title];
                        return (
                          <label
                            key={idx}
                            className={`block cursor-pointer p-3 border transition-colors ${checked ? "bg-[#FF4D00]/5 border-[#FF4D00]/40" : "bg-[#0F0F0F] border-white/10 hover:border-white/20"}`}
                            data-testid={`reg-consent-clause-${idx}`}
                          >
                            <div className="flex items-start gap-2">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={e => setConsentClausesAccepted(p => ({ ...p, [title]: e.target.checked }))}
                                className="mt-0.5 w-4 h-4 accent-[#FF4D00] shrink-0"
                                data-testid={`reg-consent-clause-checkbox-${idx}`}
                              />
                              <div className="flex-1">
                                <div className="text-xs font-bold text-white">{title}</div>
                                {(cl.text || "").trim() && (
                                  <div className="text-[11px] text-gray-400 whitespace-pre-wrap mt-1 leading-relaxed">{cl.text}</div>
                                )}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  ) : null}

                  {/* Nombre/RUT tipeados */}
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div>
                      <Label className="text-[10px] uppercase text-gray-500">{lang === "en" ? "Full name (typed) *" : "Nombre completo (tipear) *"}</Label>
                      <Input className="bg-[#1A1A1A] border-white/10 mt-1 h-9 text-sm" value={consentFullName} onChange={e => setConsentFullName(e.target.value)} data-testid="reg-consent-fullname" />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase text-gray-500">RUT *</Label>
                      <Input className="bg-[#1A1A1A] border-white/10 mt-1 h-9 text-sm" value={consentRut} onChange={e => setConsentRut(e.target.value)} placeholder="12.345.678-9" data-testid="reg-consent-rut" />
                    </div>
                  </div>

                  {/* Apoderado si menor */}
                  {(Number(regForm.age) > 0 && Number(regForm.age) < 18) && (
                    <div className="grid grid-cols-2 gap-2 mb-2 p-2 bg-amber-500/5 border border-amber-500/30" data-testid="reg-consent-guardian-block">
                      <div>
                        <Label className="text-[10px] uppercase text-amber-300">{lang === "en" ? "Guardian name *" : "Apoderado *"}</Label>
                        <Input className="bg-[#1A1A1A] border-amber-500/40 mt-1 h-9 text-sm" value={guardianName} onChange={e => setGuardianName(e.target.value)} data-testid="reg-consent-guardian-name" />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase text-amber-300">{lang === "en" ? "Guardian RUT *" : "RUT apoderado *"}</Label>
                        <Input className="bg-[#1A1A1A] border-amber-500/40 mt-1 h-9 text-sm" value={guardianRut} onChange={e => setGuardianRut(e.target.value)} placeholder="12.345.678-9" data-testid="reg-consent-guardian-rut" />
                      </div>
                    </div>
                  )}

                  {/* Contacto de emergencia */}
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    <div>
                      <Label className="text-[10px] uppercase text-gray-500">{lang === "en" ? "Emergency name *" : "Emergencia (nombre) *"}</Label>
                      <Input className="bg-[#1A1A1A] border-white/10 mt-1 h-9 text-sm" value={emerName} onChange={e => setEmerName(e.target.value)} data-testid="reg-consent-emer-name" />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase text-gray-500">{lang === "en" ? "Phone *" : "Teléfono *"}</Label>
                      <Input className="bg-[#1A1A1A] border-white/10 mt-1 h-9 text-sm" value={emerPhone} onChange={e => setEmerPhone(e.target.value)} data-testid="reg-consent-emer-phone" />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase text-gray-500">{lang === "en" ? "Relation" : "Parentesco"}</Label>
                      <Input className="bg-[#1A1A1A] border-white/10 mt-1 h-9 text-sm" value={emerRel} onChange={e => setEmerRel(e.target.value)} placeholder={lang === "en" ? "Father, mother..." : "Padre, madre..."} data-testid="reg-consent-emer-rel" />
                    </div>
                  </div>

                  {/* Firma dibujada */}
                  <div className="mb-2">
                    <SignaturePad label={lang === "en" ? "Signature *" : "Firma dibujada *"} onChange={setConsentSignature} data-testid="reg-consent-signature" />
                  </div>

                  {/* Checkbox global (solo si NO hay cláusulas) */}
                  {(event.consent_clauses || []).length === 0 && (
                    <label className="flex items-start gap-2 cursor-pointer select-none mb-1">
                      <input
                        type="checkbox"
                        checked={consentAccepted}
                        onChange={e => setConsentAccepted(e.target.checked)}
                        className="mt-0.5 w-4 h-4 accent-[#FF4D00]"
                        data-testid="reg-consent-checkbox"
                      />
                      <span className="text-xs text-gray-300">
                        {lang === "en"
                          ? "I have read and accept the informed consent."
                          : "He leído y acepto el consentimiento informado."}
                      </span>
                    </label>
                  )}
                </div>
              )}

              <Button className={`w-full ${distIsFree ? "bg-[#16A34A] hover:bg-[#15803D]" : "bg-[#FF4D00] hover:bg-[#E64500]"} text-white font-bold h-12`} disabled={regLoading} data-testid="reg-submit-btn"
                onClick={async () => {
                  // Validacion: campos basicos siempre + campos configurados como requeridos
                  const required = event.registration_fields || [];
                  if (!regForm.full_name || !regForm.email || !regForm.phone) { toast.error(lang === "en" ? "Fill required fields" : "Completa nombre, email y telefono"); return; }
                  for (const f of required) {
                    const val = f === "edad" ? regForm.age : f === "ciudad" ? regForm.city : f === "talla_remera" ? regForm.shirt_size : regForm[f];
                    if (!val || (typeof val === "string" && val.trim() === "")) {
                      toast.error(lang === "en" ? "Fill all required fields" : "Completa todos los campos obligatorios"); return;
                    }
                  }
                  // Validación de adjuntos requeridos
                  const requiredAtt = (event?.registration_attachments || []).filter(a => a && a.required && (a.key || "").trim());
                  for (const slot of requiredAtt) {
                    if (!attachments[slot.key] || !attachments[slot.key].content) {
                      toast.error((lang === "en" ? "Missing document: " : "Falta adjuntar: ") + (slot.label || slot.key));
                      return;
                    }
                  }
                  // Validación del consentimiento si aplica
                  const hasClauses = (event?.consent_clauses || []).length > 0;
                  const hasConsent = hasClauses || (event?.consent_text || "").trim();
                  if (hasConsent) {
                    if (hasClauses) {
                      const required = (event.consent_clauses || []).map(c => (c.title || "").trim()).filter(Boolean);
                      const missing = required.filter(t => !consentClausesAccepted[t]);
                      if (missing.length > 0) { toast.error((lang === "en" ? "Missing acceptance: " : "Falta aceptar: ") + missing.join(", ")); return; }
                    } else {
                      if (!consentAccepted) { toast.error(lang === "en" ? "You must accept the consent" : "Debes aceptar el consentimiento"); return; }
                    }
                    if (!consentSignature) { toast.error(lang === "en" ? "Please sign in the box" : "Por favor firma en el recuadro"); return; }
                    if (!consentFullName.trim() || !consentRut.trim()) { toast.error(lang === "en" ? "Type your full name and RUT" : "Escribe nombre y RUT"); return; }
                    if (!emerName.trim() || !emerPhone.trim()) { toast.error(lang === "en" ? "Emergency contact required" : "Completa contacto de emergencia"); return; }
                    if (Number(regForm.age) > 0 && Number(regForm.age) < 18) {
                      if (!guardianName.trim() || !guardianRut.trim()) {
                        toast.error(lang === "en" ? "Minor requires guardian info" : "Menor de edad requiere datos del apoderado"); return;
                      }
                    }
                  }
                  setRegLoading(true);
                  try {
                    const payload = {
                      event_id: event.id, event_title: event.title, distance_label: selectedDist.label,
                      distance_price: selectedDist.price, ...regForm, age: Number(regForm.age) || 0, discount_code: discountCode
                    };
                    if ((event?.registration_attachments || []).length > 0) {
                      payload.attachments = attachments;
                    }
                    if (event?.requires_access_code) {
                      payload.access_code_used = (accessCode || "").trim().toUpperCase();
                    }
                    if (hasConsent) {
                      payload.consent_accepted = !hasClauses ? true : false;
                      payload.consent_clauses_accepted = hasClauses ? Object.keys(consentClausesAccepted).filter(k => consentClausesAccepted[k]) : [];
                      payload.consent_signature = consentSignature;
                      payload.consent_full_name_typed = consentFullName.trim();
                      payload.consent_rut_typed = consentRut.trim();
                      payload.consent_guardian_name = guardianName.trim();
                      payload.consent_guardian_rut = guardianRut.trim();
                      payload.emergency_contact_name = emerName.trim();
                      payload.emergency_contact_phone = emerPhone.trim();
                      payload.emergency_contact_relation = emerRel.trim();
                    }
                    const { data } = await axios.post(`${API}/event-registrations`, payload);
                    if (data.checkout_url) { window.location.href = data.checkout_url; }
                    else {
                      setRegOpen(false);
                      navigate("/inscripcion-exitosa", {
                        state: {
                          registration_id: data.registration_id,
                          event_id: event.id,
                          event_title: event.title,
                          distance_label: selectedDist.label,
                          full_name: regForm.full_name,
                          email: regForm.email,
                          final_price: data.final_price || 0,
                          status: "confirmed",
                        }
                      });
                    }
                  } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
                  finally { setRegLoading(false); }
                }}
              >
                {regLoading ? "..." : (distIsFree ? t("confirm_free_registration") : t("confirm_registration"))}
              </Button>
            </div>
          </div>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
}
